import { useMemo, useCallback } from "react";
import { Plus, Trash2 } from "lucide-react";

/**
 * Free Rent period entry for the detail panel tab.
 * Each period has start/end dates, computed duration, type, and optional partial amount.
 */

export interface FreeRentPeriod {
  id: string;
  start_date: string;
  end_date: string;
  type: "full" | "partial";
  partial_amount: number | null;
}

interface FreeRentTabProps {
  periods: FreeRentPeriod[];
  onPeriodChange: (id: string, key: keyof FreeRentPeriod, value: unknown) => void;
  onPeriodAdd: () => void;
  onPeriodDelete: (id: string) => void;
}

function computeDurationMonths(start: string, end: string): number {
  if (!start || !end) return 0;
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || e <= s) return 0;
  const months =
    (e.getFullYear() - s.getFullYear()) * 12 +
    (e.getMonth() - s.getMonth()) +
    (e.getDate() >= s.getDate() ? 0 : -1);
  return Math.max(0, months);
}

export default function FreeRentTab({
  periods,
  onPeriodChange,
  onPeriodAdd,
  onPeriodDelete,
}: FreeRentTabProps) {
  const rows = useMemo(
    () =>
      periods.map((p) => ({
        ...p,
        duration_months: computeDurationMonths(p.start_date, p.end_date),
      })),
    [periods],
  );

  const handleChange = useCallback(
    (id: string, key: keyof FreeRentPeriod, value: unknown) => {
      onPeriodChange(id, key, value);
    },
    [onPeriodChange],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-bark-400">
          Free Rent Periods
        </h3>
        <button
          type="button"
          onClick={onPeriodAdd}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-forest-700 text-white rounded-lg hover:bg-forest-800 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Free Rent Period
        </button>
      </div>

      <div className="bg-white border border-grove-300 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-grove-100 border-b border-grove-300">
                <th className="px-3 py-2 text-xs font-semibold text-bark-500 uppercase tracking-wider text-left w-[130px]">
                  Start Date
                </th>
                <th className="px-3 py-2 text-xs font-semibold text-bark-500 uppercase tracking-wider text-left w-[130px]">
                  End Date
                </th>
                <th className="px-3 py-2 text-xs font-semibold text-bark-500 uppercase tracking-wider text-right w-[100px]">
                  Duration (mo)
                </th>
                <th className="px-3 py-2 text-xs font-semibold text-bark-500 uppercase tracking-wider text-left w-[120px]">
                  Type
                </th>
                <th className="px-3 py-2 text-xs font-semibold text-bark-500 uppercase tracking-wider text-right w-[120px]">
                  Partial Amount
                </th>
                <th className="w-10 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-sm text-bark-400"
                  >
                    No free rent periods configured. Click "+ Add Free Rent Period"
                    to add one.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-grove-200 last:border-0"
                  >
                    <td className="px-3 py-1.5">
                      <input
                        type="date"
                        value={row.start_date}
                        onChange={(e) =>
                          handleChange(row.id, "start_date", e.target.value)
                        }
                        className="w-full px-2 py-1 text-sm border border-grove-300 rounded-lg text-bark-700 focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <input
                        type="date"
                        value={row.end_date}
                        onChange={(e) =>
                          handleChange(row.id, "end_date", e.target.value)
                        }
                        className="w-full px-2 py-1 text-sm border border-grove-300 rounded-lg text-bark-700 focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                      />
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <span className="text-sm text-bark-600 tabular-nums">
                        {row.duration_months > 0 ? row.duration_months : "--"}
                      </span>
                    </td>
                    <td className="px-3 py-1.5">
                      <select
                        value={row.type}
                        onChange={(e) =>
                          handleChange(row.id, "type", e.target.value)
                        }
                        className="w-full px-2 py-1 text-sm bg-white border border-grove-300 rounded-lg text-bark-700 focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                      >
                        <option value="full">Full</option>
                        <option value="partial">Partial</option>
                      </select>
                    </td>
                    <td className="px-3 py-1.5">
                      {row.type === "partial" ? (
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-bark-400">
                            $
                          </span>
                          <input
                            type="number"
                            step="any"
                            value={row.partial_amount ?? ""}
                            onChange={(e) =>
                              handleChange(
                                row.id,
                                "partial_amount",
                                e.target.value ? Number(e.target.value) : null,
                              )
                            }
                            placeholder="0.00"
                            className="w-full pl-5 pr-2 py-1 text-sm text-right border border-grove-300 rounded-lg text-bark-700 tabular-nums focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                          />
                        </div>
                      ) : (
                        <span className="block text-right text-sm text-bark-300">
                          --
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <button
                        type="button"
                        onClick={() => onPeriodDelete(row.id)}
                        className="p-1 rounded text-bark-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                        title="Delete period"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-bark-300">
        Duration is auto-computed from the start and end dates. Partial free rent
        reduces rent to the specified amount instead of full abatement.
      </p>
    </div>
  );
}
